import { useMemo, useState } from 'react'

import { F0Dialog, F0TagPerson, F0Text } from '@factorialco/f0-react'
import { Stack } from '@factorialco/f0-react/dist/experimental'
import {
  CreateTicketingResolverTeamDocument,
  TicketingResolverTeamsDocument,
  TicketingTeamMembersDocument,
  type TicketingTeamMembersQuery,
  type TicketingTeamMembersQueryVariables,
  type TicketingResolverTeamsQuery,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { TeamSelector, type Team } from 'modules/ticketing/components/TeamSelector'

interface Props {
  onClose: () => void
}

const AssignTeamDialog = ({ onClose }: Props) => {
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data: membersData } = useQuery<
    TicketingTeamMembersQuery,
    TicketingTeamMembersQueryVariables
  >(TicketingTeamMembersDocument, {
    variables: { teamIds: selectedTeam ? [selectedTeam.id] : [] },
    skip: !selectedTeam,
  })

  const { data: resolverTeamsData } = useQuery<TicketingResolverTeamsQuery, Record<string, never>>(
    TicketingResolverTeamsDocument
  )

  const alreadyAddedTeamIds = useMemo(
    () =>
      (resolverTeamsData?.ticketing?.resolverTeamsConnection?.nodes ?? []).map((rt) =>
        String(rt.teamId)
      ),
    [resolverTeamsData]
  )

  const memberNames = useMemo(
    () =>
      (membersData?.teams?.memberships ?? [])
        .filter((m) => m.employeeName?.fullName)
        .map((m) => m.employeeName?.fullName ?? ''),
    [membersData]
  )

  const [performCreate] = useMutation(CreateTicketingResolverTeamDocument, {
    extractorFn: (data) => data.ticketingMutations.createTicketingResolverTeam,
    refetchQueries: [TicketingResolverTeamsDocument],
  })

  const handleSubmit = async () => {
    if (!selectedTeam) return
    setSubmitting(true)
    setError(null)

    const result = await performCreate({
      variables: { teamId: selectedTeam.id },
    })
    const handled = handleResult(result, () => {})
    setSubmitting(false)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      onClose()
    } else {
      const errors = handled.errors ?? []
      const isDuplicate = errors.some(
        (e) => '__typename' in e && e.__typename === 'StructuredError' && e.field === 'team_id'
      )
      setError(
        isDuplicate
          ? i18n.t('ticketing.settings.resolver_teams.already_added')
          : i18n.t('errors.generic_error')
      )
    }
  }

  return (
    <F0Dialog
      isOpen
      width="sm"
      title={i18n.t('ticketing.settings.resolver_teams.assign_dialog_title')}
      onClose={onClose}
      primaryAction={{
        label: i18n.t('ticketing.settings.resolver_teams.assign_button'),
        onClick: handleSubmit,
        disabled: !selectedTeam || submitting,
        loading: submitting,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <Stack paddingX="p-4" paddingY="p-4" gap="4">
        <F0Text
          content={i18n.t('ticketing.settings.resolver_teams.assign_dialog_description')}
          variant="description"
        />
        <F0Text
          content={i18n.t('ticketing.settings.resolver_teams.assign_dialog_description_hint')}
          variant="description"
        />
        <TeamSelector
          value={selectedTeam}
          onSelect={setSelectedTeam}
          label={i18n.t('ticketing.settings.resolver_teams.select_team')}
          mandatory
          disabledIds={alreadyAddedTeamIds}
        />
        {selectedTeam && (
          <Stack gap="2">
            <F0Text
              variant="label"
              content={`${i18n.t('ticketing.settings.resolver_teams.members_preview_title')} (${memberNames.length})`}
            />
            {memberNames.length === 0 ? (
              <F0Text
                variant="description"
                content={i18n.t('ticketing.settings.resolver_teams.members_empty')}
              />
            ) : (
              <Stack gap="2">
                {memberNames.map((name) => (
                  <F0TagPerson key={name} name={name} />
                ))}
              </Stack>
            )}
          </Stack>
        )}
        {error && <F0Text content={error} color="critical" />}
      </Stack>
    </F0Dialog>
  )
}

export default AssignTeamDialog
